<script setup>
import { computed } from 'vue';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { isUnseen } from 'dashboard/store/modules/conversations/helpers';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MessagePreview from './MessagePreview.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import UnreadBadge from 'dashboard/components-next/Conversation/ConversationCard/UnreadBadge.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';
// BonBoZ: Avatar nhận `size` là số nên cỡ avatar phải tính bằng JS, không class.
import { usePancakeMetrics } from 'dashboard/composables/usePancakeMetrics';
import { coSoDienThoai } from 'dashboard/helper/quickFilters';
import { useDateFilterMode } from 'dashboard/composables/useDateFilterMode';

const props = defineProps({
  chat: { type: Object, required: true },
  currentContact: { type: Object, required: true },
  assignee: { type: Object, default: () => ({}) },
  isActiveChat: { type: Boolean, default: false },
  showAssignee: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
});

defineEmits(['click', 'contextmenu']);

const { cardAvatarSize } = usePancakeMetrics();
const { showCreatedAt, showLastVisibleMessageAt } = useDateFilterMode();

// "Updated" date filter on: show the time it matched on. Falls back to last
// activity when the server has not sent the field yet.
const cardTimestamp = computed(() =>
  showLastVisibleMessageAt.value && props.chat.last_visible_message_at
    ? props.chat.last_visible_message_at
    : props.chat.timestamp
);

// Dung chung phep kiem voi bo loc 'Co SDT' — hai cho noi khac nhau la mat tin.
const coSdt = computed(() => coSoDienThoai(props.chat));

const unreadCount = computed(() => props.chat.unread_count);
const lastMessageInChat = computed(() => getLastMessage(props.chat));
// BonBoZ: thay vi tu suy ra, doc thang cot `unseen_since` server gui xuong -
// cung con so ma badge tab Hoi thoai dang dem.
const hasUnread = computed(() => isUnseen(props.chat));

const voiceCallData = computed(() => {
  const last = lastMessageInChat.value;
  if (last?.content_type !== 'voice_call' || !last.call) {
    return { status: null, direction: null };
  }
  return {
    status: last.call.status,
    direction: last.call.direction === 'outgoing' ? 'outbound' : 'inbound',
  };
});

// BonBoZ: bỏ `chat.priority` — nó đổi theo từng hội thoại nên hàng meta khi ẩn
// khi hiện làm card lệch chiều cao; icon ưu tiên đã chuyển vào hàng tên.
// BonBoZ: bỏ tên trang khỏi hàng meta — trang chính đã lo việc chọn trang.
const showMetaSection = computed(() => {
  return props.showAssignee && Boolean(props.assignee.name);
});

const isAgentBotAssignee = computed(
  () => props.chat?.meta?.assignee_type === 'AgentBot'
);

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !props.currentContact?.blocked
);

const messagePreviewClass = computed(() => {
  return [
    // BonBoZ: `bonboz-chua-doc` cho SCSS. KHONG dung `.font-medium` lam selector
    // trong SCSS: Chatwoot khai `h1-h6 { @apply font-medium }` nen Tailwind
    // nhan ban moi luat `.x.font-medium` thanh `h1.x, h2.x, ...` — the nao
    // cung dam. Do duoc 10/09/2026.
    hasUnread.value
      ? 'font-medium bonboz-chua-doc text-n-slate-12'
      : 'text-n-slate-11',
    !props.compact && hasUnread.value ? 'ltr:pr-4 rtl:pl-4' : '',
    props.compact && hasUnread.value ? 'ltr:pr-6 rtl:pl-6' : '',
  ];
});
</script>

<template>
  <div
    class="relative flex items-start flex-grow-0 flex-shrink-0 w-auto max-w-full py-0 cursor-pointer conversation hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 group hover:z-[1]"
    :class="{
      // BonBoZ: nền card đang chọn theo Pancake; `!` để thắng nền chưa đọc.
      'active animate-card-select !bg-[rgb(var(--pk-hang-chon))]': isActiveChat,
      'px-0': compact,
      'px-3': !compact,
      // BonBoZ: nền xanh nhạt cho hội thoại chưa đọc — chữ đậm không đủ nổi.
      'bg-n-brand/[0.06] dark:bg-n-brand/[0.10]': hasUnread,
    }"
    @click="$emit('click', $event)"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <!-- BonBoZ: vạch chưa đọc, vẫn thấy khi card đang được chọn. -->
    <span
      v-if="hasUnread"
      class="absolute inset-y-1 left-0 w-[3px] rounded-e-full bg-n-brand"
      aria-hidden="true"
    />
    <!-- BonBoZ: badge chưa đọc chồng lên avatar, kiểu Pancake. -->
    <div class="bonboz-o-avatar relative flex-shrink-0 mt-3">
      <Avatar
        v-if="!hideThumbnail"
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="cardAvatarSize"
        :status="currentContact.availability_status"
        hide-offline-status
      />
      <UnreadBadge
        v-if="hasUnread"
        :count="unreadCount"
        class="absolute -bottom-1 -right-1"
      />
    </div>
    <!-- BonBoZ: đường kẻ đặt ở thân card để nó bắt đầu sau avatar; card chưa
         đọc dùng biến thể đậm để tách khỏi nền xanh nhạt. -->
    <div
      class="bonboz-than px-0 py-3 flex-1 min-w-0 border-b border-line"
      :class="
        hasUnread
          ? 'border-[rgb(var(--pk-vien-dam))]'
          : 'border-[rgb(var(--pk-vien))]'
      "
    >
      <div
        v-if="showMetaSection"
        class="flex items-center min-w-0 gap-1"
        :class="{
          'ltr:ml-2 rtl:mr-2': !compact,
          'mx-2': compact,
        }"
      >
        <span
          v-if="showAssignee && assignee.name"
          class="text-n-slate-11 text-xs font-medium leading-3 py-0.5 px-0 inline-flex items-center gap-px truncate"
        >
          <Icon
            :icon="isAgentBotAssignee ? 'i-lucide-bot' : 'i-lucide-user-round'"
            class="size-3 text-n-slate-11 flex-shrink-0"
          />
          <span class="truncate">{{ assignee.name }}</span>
        </span>
      </div>
      <!-- BonBoZ: tên và giờ cùng một hàng; icon ưu tiên vào đây để hàng meta
           không lúc có lúc không làm card lệch chiều cao. -->
      <div class="bonboz-hang-ten flex items-center gap-2 mx-2 h-5">
        <h4
          class="conversation--user bonboz-ten-khach text-sm my-0 capitalize text-ellipsis overflow-hidden whitespace-nowrap flex-1 min-w-0 text-n-slate-12"
          :class="hasUnread ? 'font-medium bonboz-chua-doc' : 'font-normal'"
        >
          {{ currentContact.name }}
        </h4>
        <CardPriorityIcon
          :priority="chat.priority"
          class="flex-shrink-0 !size-3.5"
        />
        <TimeAgo
          :last-activity-timestamp="cardTimestamp"
          :created-at-timestamp="chat.created_at"
          :conversation-id="chat.id"
          :show-created-at="showCreatedAt"
          class="bonboz-gio flex-shrink-0 !text-[rgb(var(--pk-gio))]"
        />
      </div>
      <VoiceCallStatus
        v-if="voiceCallData.status"
        key="voice-status-row"
        :status="voiceCallData.status"
        :direction="voiceCallData.direction"
        :message-preview-class="messagePreviewClass"
      />
      <MessagePreview
        v-else-if="lastMessageInChat"
        key="message-preview"
        :message="lastMessageInChat"
        :max-length="60"
        class="bonboz-xem-truoc my-0 mx-2 leading-6 h-6 flex-1 min-w-0 text-sm"
        :class="messagePreviewClass"
      />
      <p
        v-else
        key="no-messages"
        class="bonboz-xem-truoc text-n-slate-11 text-sm my-0 mx-2 leading-6 h-6 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
        :class="messagePreviewClass"
      >
        <fluent-icon
          size="16"
          class="-mt-0.5 align-middle inline-block text-n-slate-10"
          icon="info"
        />
        <span class="mx-0.5">
          {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
        </span>
      </p>
      <!-- BonBoZ: hàng thẻ luôn giữ chỗ, khoá đúng một dòng — virtualizer nhảy
           nếu card đổi chiều cao theo số thẻ; mỗi nấc màn hình vẫn là một chiều
           cao cố định. -->
      <div
        class="bonboz-hang-the flex items-center justify-between gap-1 mx-2 h-[18px] 2xl:h-5 3xl:h-[22px] overflow-hidden flex-nowrap"
      >
        <CardLabels
          :conversation-labels="chat.labels"
          solid
          class="flex-1 min-w-0"
        >
          <template v-if="hasSlaPolicyId" #before>
            <SLACardLabel :chat="chat" class="ltr:mr-1 rtl:ml-1" />
          </template>
        </CardLabels>
        <!-- Hai dau hieu cung o mot hop size-4 nen luon thang hang nhau. -->
        <div class="flex items-center flex-shrink-0 gap-1">
          <span
            v-if="coSdt"
            class="flex items-center justify-center text-white rounded size-4 bg-[rgb(var(--pk-sdt))]"
            :title="$t('BONBOZ.LOC_NHANH.CO_SDT')"
          >
            <Icon icon="i-lucide-phone" class="size-2.5" aria-hidden="true" />
          </span>
          <!-- BonBoZ: dấu hiệu chưa đọc, không bấm được — mở hội thoại mới là đã đọc. -->
          <span
            class="flex items-center justify-center size-4"
            :class="hasUnread ? 'text-n-ruby-9' : 'text-n-slate-10'"
          >
            <Icon
              :icon="hasUnread ? 'i-lucide-mail' : 'i-lucide-mail-open'"
              class="size-4"
              aria-hidden="true"
            />
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
